import { useCallback, useMemo } from 'react';

import { ResponseError } from 'services/api/base';
import { useGetFileListQuery, useLazyGetFileListQuery } from 'services/api/fileViewer';
import { FileList, FileListArgs } from 'helper/products/fileViewer';
import { isCompanyId } from 'helper/products/fileViewerCompanies';
import usePermissionCheck from '../usePermissionCheck';
import useHandleRequestErrors from '../useHandleRequestErrors';

/**
 * Idade da lista (s) a partir da qual voltar à tela pede de novo. Mais nova que isto sai do cache
 * sem request; mais velha sai do cache e atualiza por trás; sem cache (1 h fora da tela, ver
 * `keepUnusedDataFor`) volta o esqueleto.
 */
export const REFETCH_AFTER_SECONDS = 300;

export interface UseFileListOptions {
  /**
   * Argumentos da lista. `empresa` (admin) é outra entrada de cache: trocar de empresa mostra o
   * esqueleto até a lista dela chegar, nunca a árvore da anterior. Sem ela, a lista de hoje.
   */
  args?: FileListArgs;
  /** Não pede a lista (além de esperar a product_key). */
  skip?: boolean;
}

const NO_ARGS: FileListArgs = {};

export interface RefreshOptions {
  /** Admin: pede ao backend para reler o disco (`refresh=1`) em vez de servir o cache. */
  force?: boolean;
}

export default function useFileList({ args, skip = false }: UseFileListOptions = {}) {
  const keySaved = usePermissionCheck('file-viewer');
  // Uma entrada de cache por empresa válida, e a mesma (`{}`) para "sem empresa", `empresa: 0`
  // ou inválida (que nem iriam na URL). Identidade estável por empresa: o `refresh` não muda a
  // cada render de quem passa `{ args: { empresa } }` como literal.
  const empresa = args?.empresa;
  const queryArgs = useMemo<FileListArgs>(
    () => (isCompanyId(empresa) ? { empresa } : NO_ARGS),
    [empresa],
  );
  // Assinatura do cache: segura a entrada enquanto a tela está montada e decide, pela idade da
  // lista, se pede de novo ao montar. Sem product_key ainda, não pede. `currentData` (e não
  // `data`) porque é a lista DESTES argumentos: ao trocar de empresa fica `undefined` até a nova
  // chegar; numa atualização da mesma empresa continua a lista atual.
  const {
    currentData: data, error, isFetching, fulfilledTimeStamp,
  } = useGetFileListQuery(queryArgs, {
    skip: !keySaved || skip,
    refetchOnMountOrArgChange: REFETCH_AFTER_SECONDS,
  });
  // Só para o `refresh()`: mesma entrada de cache (mesmo endpoint, mesmos args), request forçada.
  const [trigger] = useLazyGetFileListQuery();

  useHandleRequestErrors(error as ResponseError);

  /**
   * Pede a árvore de novo, com os argumentos atuais, e devolve a resposta (tokens de
   * download/upload renovados); a lista atual continua em `data` até a nova chegar. Chamadas
   * simultâneas compartilham a mesma request; falha devolve `undefined` (o erro já segue o
   * tratamento global via `useHandleRequestErrors`).
   */
  const refresh = useCallback(async (
    { force = false }: RefreshOptions = {},
  ): Promise<FileList | undefined> => {
    try {
      return await trigger(force ? { ...queryArgs, refresh: true } : queryArgs, false).unwrap();
    } catch {
      return undefined;
    }
  }, [trigger, queryArgs]);

  return {
    data,
    /** Nada para mostrar ainda: esperando a product_key, primeira carga ou falha sem lista. */
    isLoading: data === undefined,
    /** Atualização em andamento com a lista já na tela. */
    isRefreshing: isFetching && data !== undefined,
    refresh,
    /** Quando a lista na tela foi recebida (ms). */
    updatedAt: fulfilledTimeStamp,
  };
}
